/**
 * SocialProof component
 * This component is used to display social proof information, including an image, caption, title, summary, and a list of metrics.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import './_social-proof.css'

import BlockContent from '../BlockContent'

import imageUrlBuilder from '@sanity/image-url'
import { SanityImageSource, SanityProjectDetails } from '@sanity/image-url/lib/types/types'

const sanityConfig: SanityProjectDetails = {
  projectId: String(process.env.GATSBY_SANITY_PROJECT_ID),
  dataset: String(process.env.GATSBY_SANITY_DATASET),
}

import { Serializer } from 'src/utils/serializer'

import { useHover } from 'src/hooks/useHover'
import { useMediaQuery } from 'src/hooks/useMediaQuery'
import { useAnimationFrame } from 'src/hooks/useAnimationFrame'
import { useInterceptionObserver } from 'src/hooks/useInterceptionObserver'

type SocialProofProps = MetricsContextProps & MetricGroupProps

/**
 * Context element of the social proof, who we are and why are we providing these metrcs
 */
type MetricsContextProps = {
  title: unknown[]
  image: {
    asset: {
      _id: string
      _ref?: string
    }
  }
}

const SINGLE_PIXEL_IMAGE = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="

const MetricsContext = forwardRef<HTMLDivElement, { data: MetricsContextProps }>((props, parent) => {
  const { isMobileDevice } = useMediaQuery()
  const [ imageUrl, setImageUrl ] = useState<string>(SINGLE_PIXEL_IMAGE)

  const { data: { image, title }} = props
  const imageIdentifier = image?.asset?._id || image?.asset?._ref

  const size = isMobileDevice ? 64 : 128
  const dimensions = { width: size, height: size * 0.659 }

  useEffect(() => {
    const targetUrl = imageUrlBuilder(sanityConfig)
      .image(imageIdentifier as SanityImageSource)
      .width(dimensions.width)
      .url()

    const imageSource = targetUrl || imageUrl
    setImageUrl(imageSource)
  }, [imageIdentifier])

  return (
    <div ref={parent} className='social-proof__context'>
      <div className='social-proof__context--header'>
        <picture className='social-proof__context--header--image'>
          <img
            className='lazy-image'
            src={imageUrl}
            alt=""
            width={dimensions.width}
            height={dimensions.height} />
        </picture>
      </div>

      <div className='social-proof__context--content'>
        <BlockContent blocks={title} serializers={Serializer} />
      </div>
    </div>
  )
})

/**
 * The metrics card showing the value and clarification of the positive impact provided
 */
type MetricCardProps = {
  value: number
  unit: string
  precision: string
  summary: string
  active?: boolean
}

type MetricCardControl = {
  isVisible: () => boolean,
  isActive: () => boolean,
  setActive: (active: boolean) => void
}

const MetricCard = forwardRef<MetricCardControl, { data: MetricCardProps }>((props, parent) => {
  const { data: { value: target, unit, precision, summary, active = false } } = props
  const [visible, setTarget] = useInterceptionObserver({ threshold: 0.5 })
  const [animationCompleted, setAnimationCompleted] = useState(false)
  const [enable, setEnable] = useState(active)
  const {isMobileDevice} = useMediaQuery()

  const value = isMobileDevice ? target : 0
  const valueRef = useRef(value)
  const elementRef = useRef<HTMLSpanElement>(null)

  /**
   * If the element is not visible, we don't need to update
   */
  useEffect(() => {
    if (isMobileDevice) return

    if (animationCompleted) return

    valueRef.current = visible ? 0 : valueRef.current
  }, [visible])

  useAnimationFrame((delta) => {
    if (!visible) return

    /**
     * If the element is not visible, we don't need to update
     */
    if (!elementRef.current) {
      return
    }

    /**
     * If the target value is the same as the current value, we don't need to update
     */
    if (valueRef.current === target) {
      setAnimationCompleted(true)
      return
    }

    if (animationCompleted) {
      return
    }

    const value = valueRef.current
    const increment = Math.max(0.1, (target - value) * delta / 300)
    const proposed = Math.min(value + increment, target)
    const content = `${proposed.toFixed(0)}${unit}${precision}`
    elementRef.current.innerText = content
    valueRef.current = proposed
  }, [visible, setAnimationCompleted])

  useImperativeHandle(parent, () => ({
    isVisible: () => visible,
    isActive: () => enable,
    setActive: (active: boolean) => {
      setEnable(active)
    }
  }))

  return (
    <div ref={element => setTarget(element)} className='social-proof__metrics-card' data-asktia-visible={visible} data-asktia-active={enable}>
      <span ref={elementRef} className='social-proof__metrics-card--value'>{value}{unit}{precision}</span>
      <span className='social-proof__metrics-card--summary'>{summary}</span>
    </div>
  )
})

type MetricGroupControl = {
  setScrollable: (enabled: boolean) => void
  setSelectedCard: (index: number) => void
  next: () => void
  preivous: () => void
}

type MetricGroupProps = {
  metrics: Array<MetricCardProps>
}

/**
 * A stack of cards to show
 */
const MetricGroup = forwardRef<MetricGroupControl, { data: MetricGroupProps }>((props, parent) => {
  /**
   * The list of references to the cards to show
   */
  const cards = useRef<Record<number, MetricCardControl>>({})

  const cardSectionRef = useRef<HTMLDivElement>(null)
  const [hovered, element] = useHover(cardSectionRef)

  useEffect(() => {
    const element = cardSectionRef.current

    if (!element) return

    element.classList.toggle('hovered', hovered)

    return () => {
      element.classList.remove('hovered')
    }
  }, [hovered])

  /**
   * The selected card index
   */
  const [selectedCard, setSelectedCard] = useState<number>(-1)
  const { data: { metrics } } = props

  useImperativeHandle(parent, () => ({
    setScrollable: (value: boolean) => {
      if (!cardSectionRef.current) return
      Object.assign(cardSectionRef.current.style, { 'overflow-y': value ? 'scroll' : 'hidden' })

    },
    setSelectedCard: (index: number) => {
      if (selectedCard !== index) {
        return
      }
      setSelectedCard(index)
    },
    next() {
      const currentCard = cards.current[selectedCard]
      currentCard?.setActive(false)

      const nextCardIndex = (selectedCard + 1) % metrics.length
      const nextCard = cards.current[nextCardIndex]
      nextCard.setActive(true)

      setSelectedCard(nextCardIndex)
    },
    preivous() {
      const currentCard = cards.current[selectedCard]
      currentCard?.setActive(false)

      const previousCardIndex = (selectedCard + metrics.length - 1) % metrics.length
      const previousCard = cards.current[previousCardIndex]
      previousCard.setActive(true)

      setSelectedCard(previousCardIndex)
    }
  }))

  return (
    <div ref={cardSectionRef} className='social-proof__metrics'>
      {metrics.map((metric: MetricCardProps, index: number) => (
        <div className='social-proof__metrics--item' key={index}>
          <MetricCard ref={(element) => Object.assign(cards.current, { [index]: element })} data={metric}/>
        </div>
      ))}
    </div>
  )
})

/**
 * Full width call to action module
 */
const SocialProof = (props: { data: SocialProofProps }) => {
  const { data: { image, title, metrics } } = props
  const metricsRef  = useRef<MetricGroupControl>(null)
  const [__, setScrollTarget] = useInterceptionObserver({
    threshold: 0.50,
    onChance: (visible) => {
      if (!metricsRef.current) return
      metricsRef.current.setScrollable(visible)
    }
  })

  const [_, setTarget] = useInterceptionObserver({
    threshold: 0.50,
    onChance: (visible, current) => {
      if (!visible) return /** no behavior */
      current.target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
    }
  })

  return (
    <div ref={element => setTarget(element)} className='social-proof'>
      <div className="social-proof__container">
        {/** Context section */}
        <MetricsContext ref={setScrollTarget} data={{ image, title }} />
        {/** Metrics section */}
        <MetricGroup ref={metricsRef} data={{ metrics }} />
      </div>
    </div>
  )
}

const exportables = Object.assign({}, SocialProof, {
  Context: MetricsContext,
  Metrics: MetricGroup,
  Card: MetricCard
})
export default exportables

export type { SocialProofProps, MetricsContextProps, MetricCardProps, MetricGroupProps, MetricCardControl, MetricGroupControl }

/**
 * Exporting the SocialProof component and its subcomponents
 * This allows for importing the main component and its subcomponents separately.
 * The MetricsContext and MetricGroup components are also exported for use in other parts of the application.
 * This is useful for creating a modular structure where each component can be used independently.
 * The SocialProof component is the main component that combines the Context and Metrics components.
 * The Context component is used to display the image, caption, title, and summary.
 * The MetricGroup component is used to display the list of metrics.
 * The Metric type is used to define the structure of each metric, including the title, value, and unit.
 * The SocialProofProps type is used to define the structure of the props passed to the SocialProof component.
 * The MetricsContextProps type is used to define the structure of the props passed to the Context component.
 * The MetricsProps type is used to define the structure of the props passed to the Metrics component.
 * The MetricCardProps type is used to define the structure of each metric passed to the Metrics component.
 */
export { SocialProof, MetricsContext, MetricGroup, MetricCard }
